import { Box, useMediaQuery, useTheme } from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SidebarItem from '../Sidebar/SidebarItem';
import SpeechBubblePortal from '../SpeechBubblePortal';

const STORAGE_KEY = 'admin-notification-settings-dismissed';

interface SettingsSidebarItemProps {
  expandDueToChildren: boolean;
  onHoverIntent: (hasChildren: boolean) => void;
  disabled?: boolean;
}

const SettingsSidebarItem: React.FC<SettingsSidebarItemProps> = ({
  expandDueToChildren,
  onHoverIntent,
  disabled = false,
}) => {
  const location = useLocation();
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [refReady, setRefReady] = useState(false);
  const [hasUnseenNotifications, setHasUnseenNotifications] = useState(false);
  const [unseenNotificationIds, setUnseenNotificationIds] = useState<string[]>([]);
  const sidebarItemRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile) {
      hamburgerRef.current = document.querySelector('.sidebar-hamburger');
    }
  }, [isMobile]);

  const handleDismiss = () => {
    setShowSpeechBubble(false);
    // Store the IDs of unseen notifications that were dismissed
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ notificationIds: unseenNotificationIds }));
  };

  const handleSettingsClick = () => {
    // When Settings is clicked, dismiss the speech bubble
    if (showSpeechBubble) {
      setShowSpeechBubble(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ notificationIds: unseenNotificationIds }));
    }
  };

  // Mark ref as ready once it's mounted
  useLayoutEffect(() => {
    if (sidebarItemRef.current) {
      setRefReady(true);
    }
  }, []);

  // Fetch notification subscription status to check for unseen items
  useEffect(() => {
    const checkUnseenNotifications = async () => {
      try {
        const backendService = new BackendService();
        const status = await backendService.getNotificationSubscriptionStatus();

        // Get IDs of unseen notifications
        const unseenIds = status.data
          .filter((notification) => !notification.seen)
          .map((notification) => notification.id);

        setUnseenNotificationIds(unseenIds);
        setHasUnseenNotifications(unseenIds.length > 0);
      } catch (error) {
        console.error('Failed to fetch notification subscription status:', error);
        // On error, don't show the bubble
        setHasUnseenNotifications(false);
        setUnseenNotificationIds([]);
      }
    };

    checkUnseenNotifications();
  }, []);

  // Show speech bubble if there are unseen notifications and not dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);

    // Check if current unseen notifications were already dismissed
    let shouldShow = hasUnseenNotifications;

    if (dismissed && hasUnseenNotifications) {
      try {
        const { notificationIds } = JSON.parse(dismissed);

        // Convert to Set for O(n) lookup instead of O(n²)
        const dismissedSet = new Set(notificationIds);

        // Only show if there are new unseen notifications that weren't previously dismissed
        const hasNewUnseen = unseenNotificationIds.some((id) => !dismissedSet.has(id));

        shouldShow = hasNewUnseen;
      } catch {
        // If parsing fails, treat as not dismissed
        shouldShow = hasUnseenNotifications;
      }
    }

    if (shouldShow && refReady) {
      const timer = setTimeout(() => {
        setShowSpeechBubble(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowSpeechBubble(false);
    }
  }, [hasUnseenNotifications, unseenNotificationIds, refReady]);

  return (
    <>
      <Box sx={{ display: 'inline-block', width: '100%' }}>
        <SidebarItem
          ref={sidebarItemRef}
          to="/settings"
          label="Settings"
          icon="Settings"
          selected={location.pathname.startsWith('/settings')}
          expanded={expandDueToChildren}
          onHoverIntent={onHoverIntent}
          onClick={handleSettingsClick}
          disabled={disabled}
        />
      </Box>

      <SpeechBubblePortal
        dismissable={true}
        tone="tone4"
        show={showSpeechBubble && !!(isMobile ? hamburgerRef.current : sidebarItemRef.current)}
        message="There are new admin notification types to subscribe to"
        targetRef={isMobile ? hamburgerRef : sidebarItemRef}
        onClose={handleDismiss}
      />
    </>
  );
};

export default SettingsSidebarItem;
